/**
 * Projects API service
 * Handles project management API calls
 */

import { apiClient } from "./client";
import { Project, ProjectMember } from "./types";

export const projectsService = {
  /**
   * Get all projects
   */
  getAll: () => apiClient.get<Project[]>("maat-projects"),

  /**
   * Get project by ID
   */
  getById: (id: string) => apiClient.get<Project>("maat-projects", { id }),

  /**
   * Create new project
   */
  create: (data: {
    name: string;
    description?: string;
    project_type?: string;
    status?: "planned" | "active" | "on-hold" | "completed";
    priority?: "high" | "medium" | "low";
    start_date?: string;
    target_end_date?: string;
    milestones?: Array<{ name: string; date: string }>;
    department?: string;
    budget?: number;
    currency?: string;
    billing_type?: string;
  }) => apiClient.post<Project>("maat-projects", data),

  /**
   * Update project
   */
  update: (
    id: string,
    data: {
      name?: string;
      description?: string;
      project_type?: string;
      status?: "planned" | "active" | "on-hold" | "completed";
      priority?: "high" | "medium" | "low";
      start_date?: string;
      target_end_date?: string;
      milestones?: Array<{ name: string; date: string }>;
      department?: string;
      budget?: number;
      currency?: string;
      billing_type?: string;
    },
  ) => apiClient.put<Project>("maat-projects", data, { params: { id } }),

  /**
   * Delete project
   */
  delete: (id: string) =>
    apiClient.delete<{ success: boolean }>("maat-projects", { params: { id } }),

  /**
   * Get project members
   */
  getMembers: (id: string) =>
    apiClient.get<ProjectMember[]>("maat-projects", { id, action: "members" }),

  /**
   * Add member to project
   */
  addMember: (
    id: string,
    data: { user_id: string; role?: "owner" | "admin" | "member" | "viewer" },
  ) =>
    apiClient.post<ProjectMember>("maat-projects", data, {
      params: { id, action: "add-member" },
    }),

  /**
   * Remove member from project
   */
  removeMember: (id: string, memberId: string) =>
    apiClient.delete<{ success: boolean }>("maat-projects", {
      params: { id, action: "remove-member", memberId },
    }),
};
